<template>
  <div class="container">
    <div class="flex-col mar-top-50 mar-bottom-50">
      <PageHeading :title="`Hello, ${userName}!`" :description="''" />
      <!-- dashboards section -->
      <section v-if="isLoading" class="flex-col pad-50">
        <LoadingBar />
      </section>
      <!-- Metrics section -->
      <section
        v-if="!isLoading && getMetrics.length > 0"
        class="flex-col pad-top-30"
      >
        <div class="section-header flex-row align-center">
          <h3>
            Recent Metrics
          </h3>
          <div class="flex-expand"></div>
          <router-link to="/metrics" class="btn-view-all">
            View all
            <i class="material-icons md-18 mar-left-10">arrow_forward</i>
          </router-link>
        </div>
        <!-- content grid -->
        <div
          :class="[
            'summary-metrics-grid',
            'mar-top-15',
            { 'col-3': getMetrics.length === 3 },
            { 'col-2': getMetrics.length === 2 },
          ]"
        >
          <SummaryMetricCard
            v-for="metric in getMetrics"
            :key="metric.id"
            :metric="metric"
          />
        </div>
      </section>
      <section
        v-if="!isLoading && getDashboards.length > 0"
        class="flex-col mar-top-30"
      >
        <div class="section-header flex-row align-center">
          <h3>
            Recent Consoles
          </h3>
          <div class="flex-expand"></div>
          <router-link to="/dashboards" class="btn-view-all">
            View all
            <i class="material-icons md-18 mar-left-10">arrow_forward</i>
          </router-link>
        </div>

        <div class="dashboard-contents mar-top-15">
          <DashboardCard
            v-for="dashboard in getDashboards"
            :key="dashboard.id"
            :dashboard="dashboard"
          />
        </div>
      </section>
      <NotFound
        v-if="
          !isLoading && getDashboards.length === 0 && getMetrics.length === 0
        "
        :message="
          'No Data found! Start creating Metrics and Consoles to view your summary'
        "
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SummaryMetricCard from "@/components/summary/views/SummaryMetricCard";
import DashboardCard from "@/components/console/views/ConsoleCard";

export default {
  components: {
    SummaryMetricCard,
    DashboardCard,
  },
  data() {
    return {
      userName: "",
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("User", ["getSummary"]),
    getMetrics() {
      return this.getSummary.metrics;
    },
    getDashboards() {
      return this.getSummary.dashboards;
    },
  },
  methods: {
    ...mapActions("User", ["getUserData", "fetchSummary", "signOut"]),
  },
  mounted() {
    this.isLoading = true;
    this.getUserData().then(data => {
      if (data.name) {
        this.userName = data.name.split(" ")[0];
      } else {
        this.signOut();
      }
    });

    if (
      !this.getSummary.dashboards.length > 0 &&
      !this.getSummary.metrics.length > 0
    ) {
      this.fetchSummary()
        .then(response => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
        });
    } else {
      this.isLoading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
section {
  .section-header {
    a {
      color: var(--color-primary);
      &:hover {
        color: var(--color-primary);
      }
    }
  }

  .btn-view-all {
    display: flex;
    flex-direction: row;
    align-items: center;
    background: var(--color-primary-light);
    color: var(--color-primary) !important;
    padding: 5px 10px;
    border-radius: 20px;
    transition: 0.3s all ease-in-out;

    &:hover {
      background: var(--color-primary);
      color: var(--color-secondary) !important;
    }
  }

  .dashboard-contents {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 15px;

    @include respond-below(md) {
      grid-template-columns: 1fr;
    }

    a {
      color: var(--font-color);

      &:active,
      &:focus {
        color: var(--font-color);
      }
    }

    .dashboard-item {
      background: var(--color-secondary);
      display: flex;
      flex-direction: column;
      padding: 15px;
      border-radius: 5px;

      &:hover {
        cursor: pointer;
      }

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;

        i {
          padding: 10px;
          background: var(--border-color);
          color: var(--font-color);
          border-radius: 50px;
          margin-right: 20px;
        }

        h4 {
          font-weight: bold;

          &:hover {
            cursor: pointer;
            color: var(--color-primary);
          }
        }

        .city {
          opacity: 0.7;
        }

        &.placeholder {
          i {
            width: 15px;
            height: 15px;
          }

          h4 {
            background: var(--font-color-unselected);
            width: 150px;
            padding: 7px;
            border-radius: 10px;
          }
        }
      }

      .caption-area {
        display: flex;
        flex-direction: column;
        margin-top: 30px;

        p {
          margin: 0;

          &.created-date {
            opacity: 0.5;
            padding-top: 7px;
            font-size: 1em;
          }
        }

        &.placeholder {
          p.created-by {
            width: 100px;
            background: var(--border-color);
            padding: 5px;
            border-radius: 10px;
            margin-top: 10px;
          }
          p.created-date {
            width: 80px;
            background: var(--border-color);
            padding: 5px;
            border-radius: 10px;
            margin-top: 10px;
          }
        }
      }
    }
  }
  .summary-metrics-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 15px;
    min-width: 0;

    &.col-3 {
      grid-template-columns: 1fr 1fr 1fr;
    }

    &.col-2 {
      grid-template-columns: 1fr 1fr;
    }

    @include respond-below(md) {
      grid-template-columns: 1fr 1fr !important;
    }

    @include respond-below(sm) {
      grid-template-columns: 1fr !important;
    }

    .item {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 15px;
      background: var(--color-secondary);
      border-radius: 5px;

      .title {
        opacity: 0.6;
        text-transform: capitalize;
        padding: 0 0 5px 0;
      }

      .value {
        font-size: 3em;
        padding: 25px 0 25px 0;
        font-weight: normal;
      }
    }
  }
}

.welcome-card {
  position: relative;

  .close-button {
    top: 15px;
    right: 15px;
    position: absolute;
  }

  .caption {
    opacity: 0.7;
    max-width: 500px;
  }
  .card-image {
    width: 200px;
    height: auto;
    margin: 30px;
  }
}
</style>
